import { useEffect, useRef, memo } from "react";

interface Props {
  opacity?: number;
  size?: number;
  className?: string;
}

const CELL = 64;
const COLS = 30;
const ROWS = 18;
const TOTAL = COLS * ROWS;

/**
 * Renders a grid of smiley SVGs on a canvas.
 * Random smileys light up with neon glow, some wink randomly.
 */
const AnimatedSmileyPattern = memo(({ opacity: opacityProp, size = CELL, className = "" }: Props) => {
  const isDark = typeof window !== 'undefined' && document.documentElement.classList.contains('dark');
  const opacity = opacityProp ?? (isDark ? 0.045 : 0.02);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const brightness = new Float32Array(TOTAL);
    const target = new Float32Array(TOTAL);
    // 0 = normal, >0 = winking (countdown frames)
    const winking = new Uint16Array(TOTAL);
    let viewportWidth = 0;
    let viewportHeight = 0;

    let raf: number;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const nextWidth = Math.max(1, Math.round(rect.width));
      const nextHeight = Math.max(1, Math.round(rect.height));
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      viewportWidth = nextWidth;
      viewportHeight = nextHeight;

      canvas.width = Math.max(1, Math.round(nextWidth * dpr));
      canvas.height = Math.max(1, Math.round(nextHeight * dpr));
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };

    resize();
    const resizeObserver = new ResizeObserver(() => resize());
    resizeObserver.observe(canvas);
    window.addEventListener("resize", resize);

    for (let i = 0; i < TOTAL; i++) {
      brightness[i] = Math.random() * 0.35;
      target[i] = brightness[i];
    }

    // Light up random smileys
    const interval = setInterval(() => {
      const count = 5 + Math.floor(Math.random() * 8);
      for (let i = 0; i < count; i++) {
        const idx = Math.floor(Math.random() * TOTAL);
        target[idx] = 0.85 + Math.random() * 0.15;
      }
    }, 250);

    // Random winks
    const winkInterval = setInterval(() => {
      const count = 1 + Math.floor(Math.random() * 3);
      for (let i = 0; i < count; i++) {
        const idx = Math.floor(Math.random() * TOTAL);
        winking[idx] = 40 + Math.floor(Math.random() * 30); // ~40-70 frames wink duration
        target[idx] = 0.9 + Math.random() * 0.1; // also glow when winking
      }
    }, 800);

    const drawSmiley = (x: number, y: number, s: number, alpha: number, isWinking: boolean) => {
      const cx = x + s / 2;
      const cy = y + s / 2;
      const r = s * 0.22;

      // Neon green for winking, yellow for normal
      const hue = isWinking ? "142, 72%, 55%" : "50, 100%, 65%";

      ctx.globalAlpha = 1;

      if (alpha > 0.1) {
        ctx.shadowColor = `hsla(${hue}, ${Math.min(1, alpha * 1.5)})`;
        ctx.shadowBlur = isWinking ? 18 + alpha * 30 : 12 + alpha * 25;
      } else {
        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
      }

      // Face circle
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = `hsla(${hue}, ${alpha})`;
      ctx.lineWidth = isWinking ? 2.2 : 1.8;
      ctx.stroke();

      const eyeR = s * 0.032;
      const eyeY = cy - r * 0.22;
      ctx.fillStyle = `hsla(${hue}, ${alpha})`;

      // Left eye
      ctx.beginPath();
      ctx.arc(cx - r * 0.4, eyeY, eyeR, 0, Math.PI * 2);
      ctx.fill();

      // Right eye — wink arc or open circle
      if (isWinking) {
        ctx.beginPath();
        const winkX = cx + r * 0.4;
        ctx.moveTo(winkX - eyeR * 2.2, eyeY);
        ctx.quadraticCurveTo(winkX, eyeY + eyeR * 2.5, winkX + eyeR * 2.2, eyeY);
        ctx.strokeStyle = `hsla(${hue}, ${alpha})`;
        ctx.lineWidth = 1.4;
        ctx.lineCap = "round";
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.arc(cx + r * 0.4, eyeY, eyeR, 0, Math.PI * 2);
        ctx.fill();
      }

      // Smile — kept tight within the circle
      ctx.beginPath();
      ctx.arc(cx, cy + r * 0.1, r * 0.45, 0.2 * Math.PI, 0.8 * Math.PI);
      ctx.strokeStyle = `hsla(${hue}, ${alpha})`;
      ctx.lineWidth = 1.4;
      ctx.lineCap = "round";
      ctx.stroke();

      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
    };

    const animate = () => {
      if (!viewportWidth || !viewportHeight) {
        raf = requestAnimationFrame(animate);
        return;
      }

      ctx.clearRect(0, 0, viewportWidth, viewportHeight);

      const cols = Math.ceil(viewportWidth / size) + 1;
      const rows = Math.ceil(viewportHeight / size) + 1;
      const visibilityBoost = Math.max(0.45, Math.min(1, opacity * 10));

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const idx = (row * COLS + col) % TOTAL;

          brightness[idx] += (target[idx] - brightness[idx]) * 0.06;
          target[idx] *= 0.982;

          // Decrement wink counter
          if (winking[idx] > 0) winking[idx]--;

          const isWinking = winking[idx] > 0;
          const alpha = (0.14 + brightness[idx] * 0.86) * visibilityBoost;
          drawSmiley(col * size, row * size, size, Math.min(1, alpha), isWinking);
        }
      }

      raf = requestAnimationFrame(animate);
    };

    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(interval);
      clearInterval(winkInterval);
      resizeObserver.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [opacity, size]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 block h-full w-full pointer-events-none ${className}`}
    />
  );
});

AnimatedSmileyPattern.displayName = "AnimatedSmileyPattern";
export default AnimatedSmileyPattern;
